/// <amd-module name="VSSUI/Components/SingleLayerMasterPanel/SingleLayerMasterPanel" />
import * as React from "react";
import { useObservable } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { Header, TitleSize } from "VSSUI/Header";
import { Intersection } from "VSSUI/Intersection";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { ISingleLayerMasterPanelHeaderProps, ISingleLayerMasterPanelProps } from "./SingleLayerMasterPanel.Props";

/**
 * Props-controlled implementation of MasterPanel for experiences that don't need the layering of MasterDetailsContext
 */
export const SingleLayerMasterPanel: React.FunctionComponent<ISingleLayerMasterPanelProps> = props => {
    const { className, renderContent, renderHeader, renderSearch, showOnSmallScreens = false } = props;
    const [isScrolled, setIsScrolled] = useObservable(false);
    const containerElement = React.useRef<HTMLDivElement>(null);
    const headerElement = React.useRef<HTMLDivElement>(null);
    const hiddenHeaderElement = React.useRef<HTMLDivElement>(null);
    const contentElement = React.useRef<HTMLDivElement>(null);
    const initialHeaderHeight = React.useRef<number>(0);
    const scrolledHeaderHeight = React.useRef<number>(0);

    const timerManagement = React.useRef<TimerManagement>(new TimerManagement());
    const blockingHeaderExpand = React.useRef<boolean>(false);
    const lastBlockTimeoutId = React.useRef<number>();

    const onContentScroll = () => {
        if (containerElement.current && contentElement.current) {
            const scrollTop = containerElement.current.scrollTop;

            if (isScrolled.value && !blockingHeaderExpand.current && scrollTop === 0) {
                setIsScrolled(false);
            } else if (
                !isScrolled.value &&
                // Detects thrashing: if the large header is bigger than the container AND
                // the small header is smaller than the container, we'll cause layout thrashing
                // If that check fails, we're good to go and can apply the scrolled style to the header
                !(
                    initialHeaderHeight.current + contentElement.current.clientHeight > containerElement.current.clientHeight &&
                    scrolledHeaderHeight.current + contentElement.current.clientHeight < containerElement.current.clientHeight
                )
            ) {
                blockingHeaderExpand.current = true;

                // This is done to prevent thrashing when the panel is only scrolled a little bit; the browser would reset scrollTop to 0, so we added a slight delay to allow users to scroll more smoothly
                lastBlockTimeoutId.current && timerManagement.current.clearTimeout(lastBlockTimeoutId.current);
                lastBlockTimeoutId.current = timerManagement.current.setTimeout(() => {
                    blockingHeaderExpand.current = false;
                    onContentScroll(); // Repeat process to verify
                }, 100);

                setIsScrolled(true);
            }
        }
    };

    const removeIds = (element: Element | null) => {
        if (element) {
            element.id = "";

            if (element.hasChildNodes()) {
                const children = element.children;

                for (let i = 0; i < children.length; i++) {
                    removeIds(children.item(i));
                }
            }
        }
    };

    React.useEffect(() => {
        if (headerElement.current) {
            initialHeaderHeight.current = headerElement.current.clientHeight;
        }

        if (hiddenHeaderElement.current) {
            // fix an a11y issue caused by the hidden header causing duplicate ids
            removeIds(hiddenHeaderElement.current);
            scrolledHeaderHeight.current = hiddenHeaderElement.current.clientHeight;
        }

        document.addEventListener("resize", onContentScroll);
        return () => {
            document.removeEventListener("resize", onContentScroll);
            timerManagement.current.dispose();
        };
    }, []);

    return (
        <Intersection>
            <div
                className={css(className, "bolt-master-panel flex-column flex-noshrink scroll-auto", showOnSmallScreens && "show-on-small-screens")}
                onScroll={onContentScroll}
                ref={containerElement}
            >
                {renderHeader && (
                    <>
                        <Observer isScrolled={isScrolled}>
                            {(observerProps: { isScrolled: boolean }) => (
                                <div
                                    className={css(
                                        "bolt-master-panel-header",
                                        renderSearch && "has-search",
                                        observerProps.isScrolled && "content-scrolled"
                                    )}
                                    ref={headerElement}
                                >
                                    {renderHeader()}
                                </div>
                            )}
                        </Observer>

                        {/*
                            This is an invisible version of the scrolled header
                            We need it so we can measure how big it is and use that value to prevent layout thrashing
                        */}
                        <div
                            aria-hidden="true"
                            className={css("bolt-master-panel-header hide", renderSearch && "has-search", "content-scrolled")}
                            ref={hiddenHeaderElement}
                        >
                            {renderHeader()}
                        </div>
                    </>
                )}

                <div className="bolt-master-panel-content flex-column" ref={contentElement}>
                    {renderSearch && <div className="bolt-master-panel-search">{renderSearch()}</div>}
                    {renderContent && renderContent()}
                </div>
            </div>
        </Intersection>
    );
};

export const SingleLayerMasterPanelHeader: React.FunctionComponent<ISingleLayerMasterPanelHeaderProps> = props => {
    const { title, titleAriaLevel, subTitle, onBackButtonClick } = props;

    return (
        <Header
            backButtonProps={
                onBackButtonClick
                    ? {
                          onClick: onBackButtonClick,
                          subtle: true
                      }
                    : undefined
            }
            className="bolt-master-panel-standard-header"
            description={subTitle}
            descriptionClassName="bolt-master-panel-header-subtitle bolt-master-panel-header-secondary"
            title={title}
            titleAriaLevel={titleAriaLevel}
            titleClassName="bolt-master-panel-header-title"
            titleSize={TitleSize.Large}
        />
    );
};
